/* eslint-disable max-statements */
import { BatchListItem } from '@e-origin/contracts';
import {
  Batch,
  BatchStatusEnum,
  BatchTypeEnum,
  Declarant,
  DeclarationCustomsFlowType,
  DeclarationStatus,
  EnvironmentCountries,
  getTotal,
  TemplateType,
} from '@e-origin/shared';

import { RenderAnalysisParams } from '@enums';

export const showPre = (b: Batch) =>
  getTotal(b, DeclarationStatus.NOT_SENT, DeclarationCustomsFlowType.PRE) > 0 ||
  getTotal(b, DeclarationStatus.ANALYSIS, DeclarationCustomsFlowType.PRE) > 0 ||
  getTotal(b, DeclarationStatus.REJECTED, DeclarationCustomsFlowType.PRE) > 0;

export const showPost = (b: Batch) => {
  if (b.statistics.invalidationPending === b.statistics.total) {
    return false;
  }

  const preAccteptedCount = getTotal(b, DeclarationStatus.ACCEPTED, DeclarationCustomsFlowType.PRE);

  if (b.statistics.invalidationPending > 0 && b.statistics.invalidationPending === preAccteptedCount) {
    return false;
  }

  return (
    preAccteptedCount > 0 ||
    getTotal(b, DeclarationStatus.IN_CONTROL, DeclarationCustomsFlowType.PRE) > 0 ||
    getTotal(b, DeclarationStatus.REJECTED, DeclarationCustomsFlowType.POST) > 0 ||
    getTotal(b, DeclarationStatus.NOT_SENT, DeclarationCustomsFlowType.POST) > 0 ||
    getTotal(b, DeclarationStatus.ANALYSIS, DeclarationCustomsFlowType.POST) > 0
  );
};

export const canDisplayRiskAnalysisEdit = (batch: Batch) => {
  const { status, statistics, riskAnalysis } = batch;

  const hasAnomalies = statistics?.analysisWrongDescription || statistics?.analysisWrongHsCode || statistics?.analysisWrongValue;

  return status.value === BatchStatusEnum.IN_VALIDATION || (!!riskAnalysis && hasAnomalies);
};

export const deleteAllowed = (row: BatchListItem) => {
  if (![BatchStatusEnum.SUCCESS, BatchStatusEnum.ARCHIVED].includes(row.status.value as BatchStatusEnum)) {
    return true;
  }

  if ((row.statistics?.beGateSent > 0 || row.statistics?.sent > 0) && row.templateType === TemplateType.HIGH_VALUE_H1) {
    return false;
  }

  return (
    !row.statistics?.sent &&
    !row.statistics?.accepted &&
    !row.statistics?.released &&
    !row.statistics?.notReleased &&
    !row.statistics?.invalidated &&
    !row.statistics?.preSent
  );
};

export const renderAnalysisWrongParamsFixed = (batch: BatchListItem, type: RenderAnalysisParams) => {
  if (batch.statistics) {
    const analysisWrongDescriptionFixed = batch.statistics?.analysisWrongDescriptionFixed || 0;
    const analysisWrongDescription = batch.statistics?.analysisWrongDescription || 0;

    const analysisWrongHSCodeFixed = batch.statistics?.analysisWrongHsCodeFixed || 0;
    const analysisWrongHsCode = batch.statistics?.analysisWrongHsCode || 0;

    switch (type) {
      case RenderAnalysisParams.DESCRIPTION: {
        if (analysisWrongDescriptionFixed > analysisWrongDescription) {
          return `${analysisWrongDescription}/${analysisWrongDescription}`;
        }
        return `${analysisWrongDescriptionFixed}/${analysisWrongDescription}`;
      }
      case RenderAnalysisParams.HS_CODE: {
        if (analysisWrongHSCodeFixed > analysisWrongHsCode) {
          return `${analysisWrongHsCode}/${analysisWrongHsCode}`;
        }
        return `${analysisWrongHSCodeFixed}/${analysisWrongHsCode}`;
      }
      case RenderAnalysisParams.VALUE: {
        const analysisWrongValueFixed = batch.statistics?.analysisWrongValueFixed || 0;
        const analysisWrongValue = batch.statistics?.analysisWrongValue || 0;

        return analysisWrongValueFixed > analysisWrongValue
          ? `${analysisWrongValue}/${analysisWrongValue}`
          : `${analysisWrongValueFixed}/${analysisWrongValue}`;
      }
      default:
        break;
    }
  }

  return '';
};

export const decideIfSendAllButtonIsAvailable = (row: Batch, batchView: BatchTypeEnum, declarant: Declarant, settings): boolean => {
  if (!row.statistics || settings?.isForwarder) {
    return false;
  }

  if (row.status.value === BatchStatusEnum.SENDING) {
    return false;
  }

  if (row.virtuals?.sendActionPending) {
    return false;
  }

  if ([BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(batchView)) {
    if (
      row.statistics.beGateSent === row.statistics.total &&
      row.statistics.beGateSent > row.statistics.beGateNotReleased + row.statistics.beGateReleased &&
      row.statistics.rejected === 0
    ) {
      return false;
    }

    const notInProgress = row.status.value !== BatchStatusEnum.IN_PROGRESS;
    const sentWithNoRejection =
      row.status.value !== BatchStatusEnum.SUCCESS ||
      (row.statistics?.preSent < row.statistics?.total && row.statistics?.sent < row.statistics?.total) ||
      (row.statistics?.sent === row.statistics?.total && row.statistics?.rejected > 0);

    if (notInProgress && sentWithNoRejection && declarant.virtuals?.hasExternalSystemCustoms && row.status.subStatus !== BatchStatusEnum.ANALYSIS) {
      return true;
    }

    return showPre(row) || showPost(row);
  }

  if (settings?.country === EnvironmentCountries.NL || settings?.country === EnvironmentCountries.FR) {
    return showPre(row) || showPost(row);
  }

  return row.statistics?.sent < row.statistics?.total || (row.statistics?.sent === row.statistics?.total && row.statistics?.rejected > 0);
};
